/**
 * Mega menu promo distributor.
 *
 * Promo blocks are nested inside the header menu block, so Liquid can only render them all in one
 * place: `{% content_for 'blocks' %}` inside `[data-promo-source]`. A static block's nested blocks
 * aren't readable from Liquid, so the menu item each promo belongs to can only be resolved here.
 *
 * Each card carries `data-menu-item` (handleized) and `data-menu-item-title` (lowercased raw text).
 * Every top level menu item renders an empty `[data-promo-slot]` keyed by its link handle, and the
 * mobile drawer renders a matching `[data-promo-slot-drawer]`. This script moves each card into its
 * desktop slot and clones it into its drawer slot.
 *
 * Cards matching no menu item are returned to the source container, which is off-layout, so a typo
 * reads as "my promo didn't appear" rather than a broken menu.
 *
 * Every pass is idempotent: originals are moved (a no-op when already in place) and clones are
 * rebuilt from scratch, so it can re-run freely after section hydration, editor updates and morphs.
 */

const normalize = (value) => (value || '').trim().toLowerCase();

class MegaMenuPromos extends HTMLElement {
  connectedCallback() {
    this.distribute();

    // The menu list and drawer can each be replaced by section hydration, which would drop the
    // cards we injected, so watch the whole header and redistribute when it changes.
    this.root = document.getElementById('header-component') || document.body;
    this.observer = new MutationObserver(() => this.schedule());
    this.observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  observe() {
    this.observer?.observe(this.root, { childList: true, subtree: true });
  }

  schedule() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.distribute());
  }

  /**
   * Resolve the slot a card belongs to. Matches the menu item's handle first, then its visible
   * title, so "Shop", "shop" and "shop-all" all bind to the link a merchant meant.
   *
   * @param {HTMLElement} card
   * @param {string} slotAttribute
   * @returns {HTMLElement | null}
   */
  findSlot(card, slotAttribute) {
    const handle = normalize(card.dataset.menuItem);
    const title = normalize(card.dataset.menuItemTitle);
    if (!handle && !title) return null;

    const slots = Array.from(document.querySelectorAll(`[${slotAttribute}]`));

    const byHandle = slots.find((slot) => normalize(slot.getAttribute(slotAttribute)) === handle);
    if (byHandle) return byHandle;

    return (
      slots.find((slot) => {
        const item = slot.closest('[data-link-title]') || slot.closest('li');
        const itemTitle = normalize(
          item?.dataset?.linkTitle || item?.querySelector('.menu-drawer__menu-item-text')?.textContent
        );
        return Boolean(itemTitle) && itemTitle === title;
      }) || null
    );
  }

  distribute() {
    // Our own DOM writes would otherwise retrigger the observer. disconnect() also clears any
    // records already queued, and we reconnect once the writes are done.
    this.observer?.disconnect();

    try {
      const source = this.querySelector('[data-promo-source]') || this;
      const originals = Array.from(
        document.querySelectorAll('[data-mega-menu-promo]:not([data-promo-clone])')
      );

      document.querySelectorAll('[data-promo-clone]').forEach((clone) => clone.remove());

      originals.forEach((card) => {
        const desktopSlot = this.findSlot(card, 'data-promo-slot');
        const target = desktopSlot || source;
        if (card.parentElement !== target) target.append(card);

        const drawerSlot = this.findSlot(card, 'data-promo-slot-drawer');
        if (!drawerSlot) return;

        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.setAttribute('data-promo-clone', '');
        clone.classList.remove('mega-menu-promo--desktop');
        clone.classList.add('mega-menu-promo--drawer');
        // The original owns the editor identity; a duplicate would make selection ambiguous.
        clone.removeAttribute('data-shopify-editor-block');
        drawerSlot.append(clone);
      });

      this.sizeSlots();
    } finally {
      this.observe();
    }
  }

  /**
   * Tell each filled slot how many cards it holds, so the desktop grid knows its column count.
   * The drawer stacks in a single column regardless, so it needs nothing from this.
   */
  sizeSlots() {
    document.querySelectorAll('[data-promo-slot], [data-promo-slot-drawer]').forEach((slot) => {
      const count = slot.querySelectorAll('[data-mega-menu-promo]').length;
      if (!count) return;
      slot.style.setProperty('--promo-count', String(count));
    });
  }
}

if (!customElements.get('mega-menu-promos')) {
  customElements.define('mega-menu-promos', MegaMenuPromos);
}
